import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Pause, Play, Users } from "lucide-react";
import { api, apiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import EmptyState from "@/components/common/EmptyState";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STATUSES = [
  { value: "accepting", label: "Accepting New Sponsees", dot: "#1F7A4D" },
  { value: "limited", label: "Limited Availability", dot: "#B7791F" },
  { value: "not_accepting", label: "Not Accepting New Sponsees", dot: "#B42318" },
];

export default function SponsorDashboard() {
  const { sponsorProfile, refresh } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get("/requests/incoming"), api.get("/connections")]);
      setRequests(r.data.requests);
      setConnections(c.data.connections.filter((x) => x.role === "sponsor"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Spinner label="Loading sponsor dashboard" />;

  if (!sponsorProfile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-14">
        <EmptyState
          icon={Users}
          testId="no-sponsor-profile"
          title="You have not created a sponsor profile yet"
          description="Sponsor accounts are free forever. Create your profile so members looking for support can find you."
          action={
            <Button
              onClick={() => navigate("/become-a-sponsor")}
              className="bg-[#1F6F78] hover:bg-[#185B62] text-white"
            >
              Create sponsor profile
            </Button>
          }
        />
      </div>
    );
  }

  const setStatus = async (status) => {
    setBusy(status);
    try {
      await api.patch("/sponsor-profile/availability", { availability_status: status });
      await refresh();
      toast.success("Availability updated.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const togglePause = async () => {
    setBusy("pause");
    try {
      await api.patch("/sponsor-profile/pause", { is_paused: !sponsorProfile.is_paused });
      await refresh();
      toast.success(sponsorProfile.is_paused ? "Your profile is visible again." : "Your profile is paused and hidden from the directory.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const respond = async (id, action) => {
    setBusy(id + action);
    try {
      const { data } = await api.post(`/requests/${id}/${action}`);
      toast.success(action === "accept" ? "Request accepted. A private conversation is open." : "Request declined.");
      await load();
      if (action === "accept" && data.conversation_id) {
        navigate(`/messages?c=${data.conversation_id}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Sponsor Dashboard</h1>
          <p className="mt-1.5 text-[#4B5A60]">
            Free forever, with unlimited messaging. Thank you for showing up for others.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/become-a-sponsor")}
            data-testid="edit-sponsor-profile"
            className="bg-white border-[#E3DED4]"
          >
            Edit profile
          </Button>
          <Button
            variant="outline"
            onClick={togglePause}
            disabled={busy === "pause"}
            data-testid="toggle-pause-button"
            className="bg-white border-[#E3DED4]"
          >
            {sponsorProfile.is_paused ? (
              <>
                <Play className="w-4 h-4 mr-1.5" /> Unpause profile
              </>
            ) : (
              <>
                <Pause className="w-4 h-4 mr-1.5" /> Pause profile
              </>
            )}
          </Button>
        </div>
      </div>

      {sponsorProfile.is_paused && (
        <p
          data-testid="paused-banner"
          className="mt-4 text-sm font-semibold text-[#8A5A12] bg-[#FBF0DC] border border-[#E7CF9E] rounded-xl px-4 py-3"
        >
          Your profile is paused and hidden from the directory. Existing conversations still work.
        </p>
      )}

      <section className="mt-6 sm-card p-6 bg-white">
        <h2 className="font-display text-xl font-bold text-[#1F2A2E]">I Am Currently</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatus(s.value)}
              disabled={busy === s.value}
              data-testid={`set-availability-${s.value}`}
              className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold bg-white text-left ${
                sponsorProfile.availability_status === s.value
                  ? "border-[#1F6F78] bg-[#F2FAFA] text-[#1F2A2E]"
                  : "border-[#E3DED4] text-[#4B5A60] hover:border-[#BFD9DB]"
              }`}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.dot }} />
              {s.label}
            </button>
          ))}
        </div>
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="sm-card p-6 bg-white">
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">
            Sponsorship Requests{" "}
            {requests.length > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full bg-[#D07A4A] text-white text-xs align-middle">
                {requests.length}
              </span>
            )}
          </h2>
          {requests.length === 0 ? (
            <p className="mt-3 text-sm text-[#4B5A60]">
              No pending requests. When someone asks you to sponsor them, it will show up here.
            </p>
          ) : (
            <ul className="mt-4 space-y-4">
              {requests.map((r) => (
                <li
                  key={r.id}
                  data-testid={`request-${r.id}`}
                  className="rounded-xl border border-[#E3DED4] bg-[#FBFAF7] p-4"
                >
                  <div className="flex items-start gap-3">
                    <MemberAvatar user={r.other_user} size={44} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1F2A2E]">
                        {r.other_user?.display_name} would like to connect with you about
                        sponsorship.
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#4B5A60]">
                        {r.other_user?.recovery_program ? (
                          <ProgramBadge program={r.other_user.recovery_program} />
                        ) : null}
                        <span>Looking for a sponsor</span>
                        <span>{timeAgo(r.requested_at)}</span>
                      </div>
                      {r.message ? (
                        <blockquote className="mt-2 text-sm text-[#4B5A60] border-l-2 border-[#D7EEF0] pl-3 leading-6">
                          {r.message}
                        </blockquote>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      onClick={() => respond(r.id, "accept")}
                      disabled={busy === r.id + "accept"}
                      data-testid={`accept-request-${r.id}`}
                      className="bg-[#1F7A4D] hover:bg-[#17603C] text-white"
                    >
                      Accept
                    </Button>
                    <Button
                      onClick={() => respond(r.id, "decline")}
                      disabled={busy === r.id + "decline"}
                      variant="outline"
                      data-testid={`decline-request-${r.id}`}
                      className="bg-white border-[#E3DED4]"
                    >
                      Decline
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="sm-card p-6 bg-white">
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">Current Sponsees</h2>
          {connections.length === 0 ? (
            <p className="mt-3 text-sm text-[#4B5A60]">
              You have no active sponsees yet. Accepted requests appear here.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-[#E3DED4]">
              {connections.map((c) => (
                <li
                  key={c.id}
                  data-testid={`sponsee-${c.id}`}
                  className="py-3 flex items-center gap-3"
                >
                  <MemberAvatar user={c.other_user} size={42} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1F2A2E]">
                      {c.other_user?.display_name}
                    </p>
                    <p className="text-xs text-[#4B5A60]">Connected {timeAgo(c.accepted_at)}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/messages?c=${c.conversation_id}`)}
                    className="bg-[#1F6F78] hover:bg-[#185B62] text-white"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
